import type ArticleLocal from "~/libs/interfaces/ArticleLocal"
import articleLocalFactory from "~/libs/factories/ArticleLocalFactory"
import {usePersistedStore} from "~/stores/usePersistedStore"
import type {Ref} from "vue"
import type {Recommendation} from "recombee-api-client"
import {RecommendPlacementId} from "~/libs/constants/RecommendPlacementId"
import type RecommendArticleFilters from "~/libs/interfaces/RecommendArticleFilters"
import {useRecombeeScenariosConfig} from "~/composables/useRecombeeScenariosConfig"
import {useApiIdConverter} from "~/composables/useApiIdConverter"
import {RecombeeRecommendType} from "~/libs/constants/RecombeeRecommendType"

const recombeeArticleStore = (type: RecommendPlacementId = RecommendPlacementId.HOMEPAGE_EXTERNAL, key?: string) => defineStore(`recombee-articles-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>
      limit: Ref<number>
      articles: ArticleLocal[]
      filters: RecommendArticleFilters
      recommId: Ref<string|null>
    } = {
      isLoading: ref(true),
      limit: ref(10),
      articles: reactive([]),
      filters: {},
      recommId: ref(null)
    }
    return state
  },
  getters: {
    getIsLoading(state): boolean {
      return state.isLoading
    }
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get articles with applied filters and presets
     * @param offset
     * @param limit
     * @return ArticleLocal[]
     */
    getFilteredArticles(offset: number = 0, limit: number = 10): ArticleLocal[] {
      let matchedCount = 0

      const articles = this.articles.map((article) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...article,
          isVisible: matchFilters
        }
      })
      return articles
    },

    /**
     * Get only filtered articles (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredArticles(offset: number = 0, limit: number = 10): ArticleLocal[] {
      const articles = this.getFilteredArticles(offset, limit)
      return articles.filter((article) => article.isVisible)
    },

    /**
     * Get count of getFilteredArticles
     * @return number
     */
    getFilteredArticlesCount(): number {
      const articles = this.getFilteredArticles(0, this.articles.length).filter(article => article.isVisible)
      return articles.length
    },
    /**
     * Get article by articleId
     * @param id
     */
    getArticle(id: string): ArticleLocal | undefined {
      const article = this.articles.find((article) => {
        return article.id.indexOf(id) !== -1
      })
      return article
    },
    /**
     * Get article by path
     * @param path
     */
    getArticleByPath(path: string): ArticleLocal | undefined {
      const article = this.articles.find((article) => {
        return article.path?.indexOf(path) !== -1
      })
      return article
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: RecommendArticleFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check recommend type
      if (filters?.placementId !== this.filters.placementId) {
        return true
      }

      // check item id
      if (filters?.itemId !== this.filters.itemId) {
        return true
      }

      return false
    },
    checkFiltersChanges(filters: RecommendArticleFilters): void {
      // reset articles if filter changed
      if (this.filterChanged(filters)) {
        this.filters.placementId = filters.placementId
        this.filters.itemId = filters.itemId
        this.articles = reactive([])
      }
    },
    /**
     * Fetch articles from server
     * @return {Promise<void>}
     */
    async fetchArticles(filters: RecommendArticleFilters = {}, forceRefetch: boolean = false): Promise<void> {
      if (import.meta.server) {
        return
      }

      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()

      // reset articles if filter changed
      this.checkFiltersChanges(filters)

      // no need to fetch articles
      if (!forceRefetch && this.articles.length >= this.limit && !persistedStore.needSync(this.$id)) {
        return
      }

      const scenarioConfig = useRecombeeScenariosConfig().getScenarioConfigByPlacementId(filters.placementId)

      if (!scenarioConfig) {
        logger?.error('useRecombeeArticlesStore:fetchArticles - load scenarioConfig error', {
          filters,
          storeId: this.$id,
        })
        return
      }

      let itemIdPostfix = ''
      if (scenarioConfig.type === RecombeeRecommendType.RECOMMEND_ITEMS_TO_ITEM) {
        if (!filters.itemId) {
          logger?.error('useRecombeeArticlesStore:fetchArticles - filter itemId is mandatory', {
            filters,
            storeId: this.$id,
          })
          return
        }
        const convertedItemId = useApiIdConverter().apiIdToUuid(filters.itemId)
        itemIdPostfix = `${convertedItemId}/`
      }

      this.isLoading = true

      // 1st page
      let url = `/recombee/${scenarioConfig.type}/${itemIdPostfix}${scenarioConfig.scenario}`
      const query = {
        limit: this.limit
      }

      // next page
      if (this.recommId && this.articles.length < this.limit) {
        url = `/recombee/recommend-next-items/${this.recommId}`
        query.limit = this.limit - this.articles.length
      }

      if (import.meta.client) {
        try {
          const self = this

          useFetch(url, {
            query,
            onResponse({response}) {
              self.isLoading = false
              if (response.ok !== true) {
                logger?.error('useRecombeeArticlesStore:fetchArticles:client - error', {
                  errorDetail: response,
                  fetchArticlesUrl: url,
                  fetchArticlesQuery: query,
                })
              } else {
                const data = response._data
                self.processArticlesData(data.recomms)
                self.recommId = data.recommId
                persistedStore.setLastSync(self.$id)
              }
            }
          })
        } catch (e) {
          logger?.error('useRecombeeArticlesStore:fetchArticles:client - exception', {
            errorDetail: e,
            fetchArticlesUrl: url,
            fetchArticlesQuery: query,
          })
        }
      }
    },

    async addDetailView(itemId: string): Promise<void> {
      if (!itemId) {
        return
      }

      const logger = useFrontendLogger()

      const convertedItemId = useApiIdConverter().apiIdToUuid(itemId)

      let url = `/recombee/add-detail-view/${convertedItemId}`

      if (import.meta.client) {
        try {
          const self = this

          useFetch(url, {
            method: 'POST',
            onResponse({response}) {
              self.isLoading = false
              if (response.ok !== true) {
                logger?.error('useRecombeeArticlesStore:addDetailView:client - error', {
                  errorDetail: response,
                  addDetailViewUrl: url,
                })
              }
            }
          })
        } catch (e) {
          logger?.error('useRecombeeArticlesStore:addDetailView:client - exception', {
            errorDetail: e,
            addDetailViewUrl: url,
          })
        }
      }
    },
    /**
     * Process loaded articles from GQL
     * @param data
     */
    processArticlesData(data: any[]):void {
      if (data.length) {
        this.addArticles(data, true)
      }
    },
    /**
     * Add articles to store - remove duplicities
     * @param newArticles
     * @param rewrite
     */
    addArticles(newArticles: Recommendation[], rewrite: boolean = false): void {
      newArticles.forEach((newArticle) => {
        const articleLocal = articleLocalFactory.createArticleLocalFromRecombeeArticle(newArticle)
        const foundIndex = this.articles.findIndex(article => article.id === newArticle.id)
        if (foundIndex >= 0 && rewrite) {
          this.articles.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.articles.push(articleLocal)
        }
      })

      // sort articles
      this.articles.sort((a, b) => {
        if (a.publishedDate && b.publishedDate) {
          return a.publishedDate?.getTime() > b?.publishedDate?.getTime() ? -1 : 1
        }
        return 1
      })
    },
  }
})

export const useRecommendArticlesStore = (type: RecommendPlacementId, key?: string) => {
  return recombeeArticleStore(type, key)()
}
